import { useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import type { ScheduledMeeting } from '../../contexts';
import { useSettings } from '../../contexts/SettingsContext/hooks/useSettings';
import { useToastHelpers } from '../../hooks';
import { useCalendarTypes } from '../../hooks/useCalendarTypes';
import { settingsService } from '../../services/settingsService';
import Alert from '../Alert';
import { ToastContainer } from '../Toast';
import {
  getCalendarOptionsForAccount,
  getDefaultCalendarOptionForAccount,
  SCHEDULE_MEETING_DRAFT_QUERY_KEY,
} from './helpers/constants';
import useCalendarColorOptions from './hooks/useCalendarColorOptions';
import useConfMapper from './hooks/useConfMapper';
import { useDialInData } from './hooks/useDialInData';
import useMeetingIdSelection from './hooks/useMeetingIdSelection';
import useModalBehavior from './hooks/useModalBehavior';
import usePhoneList from './hooks/usePhoneList';
import useScheduleMeetingForm from './hooks/useScheduleMeetingForm';
import useScheduleMeetingOnOpen from './hooks/useScheduleMeetingOnOpen';
import useScheduleMeetingSubmit from './hooks/useScheduleMeetingSubmit';
import CalendarSelection from './nested/form-fields/CalendarSelection';
import ColorPicker from './nested/form-fields/ColorPicker';
import DateTimeSection from './nested/form-fields/DateTimeSection';
import JoinLinkSection from './nested/form-fields/JoinLinkSection';
import MeetingIdSelect from './nested/form-fields/MeetingIdSelect';
import MeetingTitleField from './nested/form-fields/MeetingTitleField';
import PhoneSection from './nested/form-fields/PhoneSection';
import SecuritySection from './nested/form-fields/SecuritySection';
import LoadingSpinner from './nested/layout/LoadingSpinner';
import ModalFooter from './nested/layout/ModalFooter';
import ModalHeader from './nested/layout/ModalHeader';
import type { ScheduleMeetingModalProps } from './types';

const ScheduleMeetingModal: FC<ScheduleMeetingModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const toastHelpers = useToastHelpers();
  const { profile, isLoadingProfile } = useSettings();
  const defaultMeetingId = profile?.defaultMeetingId?.trim() ?? '';
  const defaultMeetingPasscode = profile?.defaultMeetingPasscode?.trim() ?? '';
  const [meeting, setMeeting] = useState<ScheduledMeeting | undefined>();
  const modalRef = useRef<HTMLDivElement>(null);

  useModalBehavior({ isOpen, onClose, modalRef });

  useEffect(() => {
    if (isOpen) {
      return;
    }
    setMeeting(undefined);
    queryClient.removeQueries({ queryKey: SCHEDULE_MEETING_DRAFT_QUERY_KEY });
  }, [isOpen, queryClient]);

  const {
    isLoading: isInitializing,
    isError: isInitializationError,
    error,
    suggestedStartDate,
  } = useScheduleMeetingOnOpen({
    isOpen,
    setMeeting,
    toastHelpers,
    skipCreation: !!defaultMeetingId,
    isProfileReady: !isLoadingProfile,
    defaultMeetingId: defaultMeetingId || undefined,
  });

  const { formData, handleInputChange } = useScheduleMeetingForm(
    meeting,
    defaultMeetingId || undefined,
    defaultMeetingPasscode || undefined,
    suggestedStartDate,
  );
  const { colorOptions, isLoading: isColorOptionsLoading } = useCalendarColorOptions(
    formData.calendarOption,
  );

  const {
    options: meetingIdOptions,
    selectedValue: selectedMeetingId,
    isLoading: isLoadingMeetingIdOptions,
    isAllocating: isAllocatingMeetingId,
    handleChange: handleMeetingIdChange,
  } = useMeetingIdSelection({
    isOpen,
    room: formData.room,
    meeting,
    setMeeting,
    toastHelpers,
    defaultMeetingId: defaultMeetingId || undefined,
  });

  const { data: accountConnections } = useCalendarTypes();

  const calendarTypeOptions = useMemo(() => {
    const settingsCalendar = settingsService.getSettings().calendar;
    const googleConnected =
      accountConnections?.google?.connected ?? settingsCalendar.google.connected;
    const microsoftConnected =
      accountConnections?.microsoft?.connected ?? settingsCalendar.microsoft.connected;
    return getCalendarOptionsForAccount(googleConnected, microsoftConnected);
  }, [accountConnections]);

  useEffect(() => {
    const allowed = new Set(calendarTypeOptions.map((o) => o.id));
    if (allowed.has(formData.calendarOption)) {
      return;
    }
    const settingsCalendar = settingsService.getSettings().calendar;
    const googleConnected =
      accountConnections?.google?.connected ?? settingsCalendar.google.connected;
    const microsoftConnected =
      accountConnections?.microsoft?.connected ?? settingsCalendar.microsoft.connected;
    handleInputChange(
      'calendarOption',
      getDefaultCalendarOptionForAccount(googleConnected, microsoftConnected),
    );
  }, [calendarTypeOptions, formData.calendarOption, accountConnections, handleInputChange]);

  useEffect(() => {
    const isProviderCalendar =
      formData.calendarOption === 'google' || formData.calendarOption === 'outlook';

    if (!isProviderCalendar) {
      if (formData.selectedColor) {
        handleInputChange('selectedColor', '');
      }
      return;
    }

    if (colorOptions.length === 0) {
      if (formData.selectedColor) {
        handleInputChange('selectedColor', '');
      }
      return;
    }

    const hasSelectedColor = colorOptions.some((color) => color.id === formData.selectedColor);
    if (!hasSelectedColor) {
      handleInputChange('selectedColor', colorOptions[0].id);
    }
  }, [formData.calendarOption, formData.selectedColor, colorOptions, handleInputChange]);

  const {
    pin,
    isLoading: isLoadingPin,
    isError: isPinError,
    error: pinError,
  } = useConfMapper(meeting?.room);
  const {
    phoneList,
    isLoading: isLoadingPhoneList,
    isError: isPhoneListError,
    error: phoneListError,
  } = usePhoneList(meeting?.room);

  const dialInData = useDialInData(
    phoneList,
    isLoadingPhoneList,
    phoneListError,
    pin,
    isLoadingPin,
    pinError,
  );

  const { handleSubmit, validationErrors, submitError, isScheduling } = useScheduleMeetingSubmit({
    formData,
    onSuccess,
    onClose,
    phoneNumber: dialInData.primaryPhoneNumber,
    pin: dialInData.pin,
    hasLicenseError: dialInData.hasLicenseError,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div role="dialog" aria-modal="true" className="schedule-meeting-modal-overlay" tabIndex={-1}>
      <div className="schedule-meeting-modal" ref={modalRef}>
        <ModalHeader onClose={onClose} />
        <div className="schedule-meeting-modal__divider" />
        <div className="schedule-meeting-modal__content">
          {isInitializing && <LoadingSpinner />}

          {isInitializationError && (
            <Alert type="error" infoTooltipMsg={error?.message}>
              Failed to allocate meeting. Please try again later.
            </Alert>
          )}

          {!(isInitializing || isInitializationError) && (
            <form onSubmit={handleSubmit} className="schedule-meeting-modal__form">
              <div className="schedule-meeting-modal__body">
                <CalendarSelection
                  options={calendarTypeOptions}
                  selectedValue={formData.calendarOption}
                  onChange={(value) => handleInputChange('calendarOption', value)}
                />
                <MeetingTitleField
                  value={formData.description}
                  onChange={(value) => handleInputChange('description', value)}
                  error={validationErrors.description}
                />
                <DateTimeSection
                  formData={formData}
                  onInputChange={handleInputChange}
                  error={validationErrors.fromDate || validationErrors.duration}
                />
                {(formData.calendarOption === 'google' ||
                  formData.calendarOption === 'outlook') && (
                  <ColorPicker
                    selectedValue={formData.selectedColor}
                    colorOptions={colorOptions}
                    isLoading={isColorOptionsLoading}
                    onChange={(value) => handleInputChange('selectedColor', value)}
                  />
                )}
                <MeetingIdSelect
                  options={meetingIdOptions}
                  selectedValue={selectedMeetingId}
                  isLoading={isLoadingMeetingIdOptions}
                  isAllocating={isAllocatingMeetingId}
                  onChange={handleMeetingIdChange}
                />
                <SecuritySection formData={formData} onInputChange={handleInputChange} />
                <JoinLinkSection room={formData.room} />
                <PhoneSection
                  isVisible
                  room={formData.room}
                  phoneList={phoneList}
                  isLoading={isLoadingPhoneList}
                  phoneListError={isPhoneListError ? phoneListError : null}
                  pin={pin}
                  isLoadingPin={isLoadingPin}
                  pinError={isPinError ? pinError : null}
                />
              </div>
              <ModalFooter
                onClose={onClose}
                isScheduling={isScheduling}
                submitError={submitError}
              />
            </form>
          )}
        </div>
      </div>

      <ToastContainer toasts={toastHelpers.toasts} onRemoveToast={toastHelpers.removeToast} />
    </div>
  );
};

export default ScheduleMeetingModal;
